import { useState } from 'react'
import { motion } from 'framer-motion'
import { Trophy } from 'lucide-react'
import type { Celebration } from './celebrationTypes'
import {
  backdropVariants,
  modalVariants,
  radialFlashVariants,
  SPRING_CELEBRATION,
  useMotionVariants,
} from '../motion/motionTokens'
import { CelebrationParticles } from './particles'

interface MomentContent {
  kicker: string
  title: string
  subtitle: string
  trophySrc?: string
}

function contentFor(c: Celebration): MomentContent {
  switch (c.kind) {
    case 'promotion':
      return {
        kicker: '¡Ascenso!',
        title: c.divisionName,
        subtitle: `Terminasteis ${c.position}º y subís de división. La afición lo celebra.`,
        trophySrc: '/assets/broadcast/trophy-league.webp',
      }
    case 'title':
      return {
        kicker: '¡Campeones de liga!',
        title: c.divisionName,
        subtitle: `Temporada ${c.season}: el título se queda en casa.`,
        trophySrc: '/assets/broadcast/trophy-league.webp',
      }
    case 'tournamentWon':
      return {
        kicker: '¡Torneo conquistado!',
        title: c.tournamentName,
        subtitle: `Vuestra pareja levanta la copa y se lleva ${c.prize.toLocaleString('es-ES')}€ de premio.`,
        trophySrc: '/assets/broadcast/trophy-tournament.webp',
      }
    case 'clubChanged':
      return {
        kicker: 'Nuevo destino',
        title: c.clubName,
        subtitle: `Te haces cargo de un nuevo proyecto en ${c.divisionName}.`,
      }
    default:
      // Banner-only kinds (achievement, objective verdicts) never reach here
      return { kicker: '', title: '', subtitle: '' }
  }
}

// Full-screen TV stage for the big moments: promotion, league title,
// tournament victory.
export function MomentOverlay({ celebration, onDismiss }: {
  celebration: Celebration
  onDismiss: () => void
}) {
  const backdrop = useMotionVariants(backdropVariants)
  const modal = useMotionVariants(modalVariants)
  const [trophyOk, setTrophyOk] = useState(true)
  const { kicker, title, subtitle, trophySrc } = contentFor(celebration)

  return (
    <motion.div
      className="moment-overlay"
      variants={backdrop}
      initial="initial"
      animate="enter"
      exit="exit"
      onClick={onDismiss}
    >
      <motion.div
        className="moment-card tx-net"
        variants={modal}
        initial="initial"
        animate="enter"
        exit="exit"
        onClick={(e) => e.stopPropagation()}
      >
        <motion.div
          variants={radialFlashVariants}
          initial="initial"
          animate="enter"
          style={{
            position: 'absolute',
            inset: '-60px',
            background: 'radial-gradient(circle, rgba(245, 197, 66, 0.45) 0%, transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <CelebrationParticles />
        <div className="moment-kicker">{kicker}</div>
        <motion.div
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ ...SPRING_CELEBRATION, delay: 0.12 }}
          style={{ position: 'relative' }}
        >
          {trophySrc && trophyOk ? (
            <img
              src={trophySrc}
              alt=""
              className="moment-stage-img"
              onError={() => setTrophyOk(false)}
            />
          ) : (
            <span
              className="moment-stage-img"
              style={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                background: 'var(--fill-soft)',
                color: 'var(--trophy-gold)',
              }}
            >
              <Trophy size={72} />
            </span>
          )}
        </motion.div>
        <div className="moment-title">{title}</div>
        <div className="moment-subtitle">{subtitle}</div>
        <button
          className="btn-angled tone-gold"
          style={{ marginTop: '0.5rem', background: 'var(--gradient-gold)', color: 'var(--gold-contrast)' }}
          onClick={onDismiss}
        >
          Continuar
        </button>
      </motion.div>
    </motion.div>
  )
}
